import { Container, ContainerProps, VisuallyHidden, YStack } from '@my/ui'
import { BaseBlock } from './blocks.types'
import { BlockToComponentMap, getSharedBlockMappings, mapExcludedPlatforms } from './block-mappings'
import { ColumnLayoutBlockType } from './column-layout-block'
import { HeadingBlockType } from './heading-block'
import { useJumpToLinksContext } from '../contexts'
import { CategoryTabsBlockType } from 'app/features/offer/blocks'
import { useAuth } from 'app/provider/auth'
import { useIFrame } from 'app/provider/iframe'
import { isViewableBy } from './utilities/viewable-check'
import { memo } from 'react'
import { Platform } from 'react-native'

const bkgColorMap = [
  { name: 'blue', value: '#D6FFFF' },
  { name: 'black', value: '#000000' },
]

export interface BlockRendererProps {
  additionalComponents?: BlockToComponentMap
  content: BaseBlock[]
  extraProps?: {
    [key: string]: any
    isColumnContent?: boolean
    numberOfColumns?: number
    isEmbedded?: boolean
  }
  componentContainerProps?: ContainerProps

  /**
   * This is used for nested blocks, to make sure that we
   * have unique keys for each block
   */
  depth?: number
  onMeasure?: (number) => void
}

export const BlockRenderer = memo((props: BlockRendererProps) => {
  const { user } = useAuth()
  const { isIframe } = useIFrame()
  const { jumpToLinks, addJumpToLink } = useJumpToLinksContext()

  const componentsMap = {
    ...getSharedBlockMappings(),
    ...(props.additionalComponents || {}),
  }

  const createBlock = (block: BaseBlock, index: number) => {
    const extraProps = {
      key: `${block.id}-${props.depth || 0}`,
      id: block.id,
      user,
      imagePriority: index < 2, // Only the first 2 block images are prioritized
      isEmbedded: isIframe,
      ...props.extraProps,
    }

    const { Component, canRender } = componentsMap[block.blockType] || {}

    if (!Component || (canRender && !canRender(block))) {
      return null
    }

    const viewabilityMeta = block[`${block.blockType}Meta`]

    // Hide the block if the user is not allowed to see it
    if (viewabilityMeta) {
      if (!isViewableBy(viewabilityMeta.viewableBy, user)) {
        return null
      }
    }

    // Make sure that we pass over our mapped components to any
    // other block renderer that we might encounter
    if (block.blockType === ColumnLayoutBlockType) {
      extraProps['components'] = componentsMap
      extraProps['depth'] = props.depth || 0
    }

    if (block.blockType === HeadingBlockType) {
      extraProps['onMeasure'] = props.onMeasure
    }

    if (block.backgroundColor) {
      const backgroundColorName = bkgColorMap.find((item) => item.name === block.backgroundColor)

      return (
        <Container
          backgroundColor={backgroundColorName ? backgroundColorName.value : '$white'}
          py={backgroundColorName ? '$3.5' : '$0'}
          $gtSm={{
            py: backgroundColorName ? '$6' : '$0',
          }}
          key={extraProps.key}
        >
          <Container size="base" gutter>
            {/* This hidden block is for use by the JumpToLinkContext */}
            <VisuallyHidden id={extraProps.id} top={-100} />
            <Component {...extraProps} {...block} />
          </Container>
        </Container>
      )
    }

    // Category tabs added manually
    if (block.blockType === CategoryTabsBlockType) {
      return null
    }

    // @ts-ignore - Dynamic blocks...
    return (
      <Container key={extraProps.key} size="base" gutter {...props.componentContainerProps}>
        {/* This hidden block is for use by the JumpToLinkContext */}
        <VisuallyHidden id={extraProps.id} top={-100} />
        <Component {...extraProps} {...block} />
      </Container>
    )
  }

  props.content?.forEach((block) => {
    if (jumpToLinks.some((link) => link.blockId === block.id)) {
      return
    }

    if (block.blockName) {
      addJumpToLink({ blockId: block.id, blockName: block.blockName })
    }
  })

  const filteredBlocks = props.content.filter(
    (block) => !mapExcludedPlatforms(block?.excludedPlatforms || {})?.includes(Platform.OS)
  )

  return (
    <YStack flex={1} gap="$4" $gtSm={{ gap: '$6' }}>
      {filteredBlocks?.map(createBlock)}
    </YStack>
  )
})
